import { useMutation, useQuery, useQueryCache } from "@pinia/colada";
import {
	CreateDistributor,
	Distributor,
	UpdateDistributor,
	useApi,
} from "@soft-db/monitoring-api-client";
import { usePermissionStore } from "../stores/permission";
import { useQueryEnabled } from "./query";

// --- Keys ---

export const distributorsKeys = {
	root: ["distributors"] as const,

	list: () => [...distributorsKeys.root, "list"] as const,

	byId: (id: string) => [...distributorsKeys.root, "id", id] as const,
};

// --- Cache effects ---

export function useUpdateCacheById() {
	const cache = useQueryCache();

	return (dist: Distributor) => {
		cache.setQueryData(distributorsKeys.byId(dist.id), dist);
	};
}

function useInvalidateListCache() {
	const cache = useQueryCache();

	return () => {
		cache.invalidateQueries({ key: distributorsKeys.list() });
	};
}

// --- Queries ---

export function useDistEnabled(...args: MaybeRefOrGetter<string | boolean>[]) {
	const { hasPermission } = usePermissionStore();

	return useQueryEnabled(...args, () => hasPermission("distributor.get"));
}

export function useDist(id: MaybeRefOrGetter<string>) {
	const api = useApi().distributor();

	const enabled = useDistEnabled(id);
	const { data: distributor, ...rest } = useQuery({
		key: () => distributorsKeys.byId(toValue(id)),
		query: () => api.fetch(toValue(id)),
		enabled,
	});

	return {
		distributor,
		data: distributor,
		...rest,
	};
}

export function useAllDists() {
	const api = useApi().distributor();

	const { data: distributors, ...rest } = useQuery({
		key: distributorsKeys.list(),
		query: () => api.fetchAll(),
		enabled: useDistEnabled(),
	});

	return {
		distributors,
		data: distributors,
		...rest,
	};
}

// --- Mutations ---
export function useCreateDist() {
	const api = useApi().distributor();

	return useMutation({
		mutation: ({ dist }: { dist: CreateDistributor }) => api.create(dist.name),
		onSuccess: useInvalidateListCache(),
	});
}

export function useUpdateDistLogo() {
	const api = useApi().distributor();

	return useMutation({
		mutation: ({ id, image }: { id: string; image: File }) => api.updateLogo(id, image),
		onSuccess: useUpdateCacheById(),
	});
}

export function useDeleteDistLogo() {
	const api = useApi().distributor();

	return useMutation({
		mutation: ({ dist }: { dist: string }) => api.deleteLogo(dist),
		onSuccess: useUpdateCacheById(),
	});
}

export function useUpdateDist() {
	const api = useApi().distributor();

	return useMutation({
		mutation: ({ id, data }: { id: string; data: UpdateDistributor }) => api.update(id, data),
		onSuccess: useUpdateCacheById(),
	});
}

export function useArchiveDist() {
	const api = useApi().distributor();

	return useMutation({
		mutation: (id: string) => api.delete(id),
		onSuccess: useInvalidateListCache(),
	});
}
